import { toAppError, type AppError } from '@repo/api-client';
import { useSession } from '@repo/api-client/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { logger } from '@repo/logger';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { describeAppError } from '@/features/common/describeAppError';
import { AuthLayout } from './AuthLayout';

const signUpSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password is too long'),
});

type SignUpValues = z.infer<typeof signUpSchema>;

export function SignUpScreen() {
  const { signUp } = useSession();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<AppError | null>(null);

  const form = useForm<SignUpValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { firstName: '', lastName: '', email: '', password: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await signUp(values);
      navigate('/onboarding/create-business', { replace: true });
    } catch (err) {
      const appError = toAppError(err);
      logger.error('sign-up.failed', appError);
      setSubmitError(appError);
    }
  });

  const errorCopy = submitError ? describeAppError(submitError) : null;

  return (
    <AuthLayout
      title="Create your studio"
      subtitle="Get paid faster with editorial invoices, AI drafts, and automatic reminders."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/sign-in" className="font-medium text-foreground underline-offset-4 hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      {errorCopy ? (
        <Alert variant="danger" className="mb-4">
          <AlertTitle>{errorCopy.title}</AlertTitle>
          <AlertDescription>{errorCopy.message}</AlertDescription>
        </Alert>
      ) : null}

      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label="First name"
            htmlFor="firstName"
            required
            error={form.formState.errors.firstName?.message}
          >
            <Input
              autoComplete="given-name"
              invalid={!!form.formState.errors.firstName}
              {...form.register('firstName')}
            />
          </Field>
          <Field
            label="Last name"
            htmlFor="lastName"
            required
            error={form.formState.errors.lastName?.message}
          >
            <Input
              autoComplete="family-name"
              invalid={!!form.formState.errors.lastName}
              {...form.register('lastName')}
            />
          </Field>
        </div>

        <Field
          label="Email"
          htmlFor="email"
          required
          error={form.formState.errors.email?.message}
        >
          <Input
            type="email"
            autoComplete="email"
            placeholder="you@studio.com"
            invalid={!!form.formState.errors.email}
            {...form.register('email')}
          />
        </Field>

        <Field
          label="Password"
          htmlFor="password"
          required
          hint="At least 8 characters."
          error={form.formState.errors.password?.message}
        >
          <Input
            type="password"
            autoComplete="new-password"
            invalid={!!form.formState.errors.password}
            {...form.register('password')}
          />
        </Field>

        <Button type="submit" size="lg" loading={form.formState.isSubmitting}>
          Create account
        </Button>
        <p className="text-xs text-muted-foreground">
          By continuing, you agree to send and receive emails from Invoyss.
        </p>
      </form>
    </AuthLayout>
  );
}
